import { useWarmUpStream } from '@features/Clip';
import { Box, Skeleton } from '@radix-ui/themes';
import {
    isDASHProvider,
    MediaPlayer,
    MediaProviderAdapter,
    PlayerSrc,
} from '@vidstack/react';
import '@vidstack/react/player/styles/base.css';
import { useEffect, useMemo, useRef } from 'react';
import { SmartPlayerProvider } from '../context/SmartPlayerContext';
import { SmartPlayerProps } from '../types';

interface DashPlayerInstance {
    destroy: () => void;
    reset: () => void;
}

type ValidMediaMimeType =
    | 'video/mp4'
    | 'video/webm'
    | 'application/x-mpegurl'
    | 'application/dash+xml';

export const SmartPlayerRoot = ({ src, media, children }: SmartPlayerProps) => {
    const dashPlayerRef = useRef<DashPlayerInstance | null>(null);

    const fps = useMemo(() => {
        if (!media?.frameRate) return 25;
        return media.frameRate.numerator / media.frameRate.denumerator;
    }, [media?.frameRate]);

    const videoSource = useMemo<PlayerSrc | null>(() => {
        if (!src) return null;
        return {
            src,
            type: (media?.mimeType || 'video/mp4') as ValidMediaMimeType,
        };
    }, [src, media?.mimeType]);

    const { isReady } = useWarmUpStream(src);

    useEffect(() => {
        const player = dashPlayerRef.current;
        return () => {
            player?.destroy();
        };
    }, []);

    const onProviderSetup = (provider: MediaProviderAdapter) => {
        if (isDASHProvider(provider)) {
            provider.config = {
                debug: { logLevel: 0, dispatchEvent: false },
                streaming: {
                    retryAttempts: {
                        MPD: 0,
                        MediaSegment: 1,
                    },
                },
                errors: {
                    recoverAttempts: 0,
                },
            };

            dashPlayerRef.current = provider.instance as DashPlayerInstance;
        }
    };

    const handleFatalError = () => {
        dashPlayerRef.current?.reset();
    };

    if (!videoSource || !isReady) {
        return (
            <Box style={{ width: '100%', height: '100%' }}>
                <Skeleton width='100%' height='100%' />
            </Box>
        );
    }

    const corsPolicy =
        media?.mimeType === 'application/dash+xml' ? 'anonymous' : undefined;

    return (
        <SmartPlayerProvider fps={fps}>
            <MediaPlayer
                src={videoSource}
                crossOrigin={corsPolicy}
                onProviderSetup={onProviderSetup}
                onError={handleFatalError}
                style={{ width: '100%', height: '100%', position: 'relative' }}
            >
                {children}
            </MediaPlayer>
        </SmartPlayerProvider>
    );
};
